import { ColorClubMatas5, Spacing16, Spacing2, Spacing24, Spacing8 } from 'autogen/design-tokens/tokens';
import type { BasketDetailedSamplesViewModel } from 'autogen/swagger/all';
import {
    Headline as HeadlineTrans,
    Paragraph as ParagraphTrans
} from 'autogen/translation-keys/trans-website-basket-samples-entrance';
import {
    AddSample,
    Description,
    Headline as HeadlineSingleTrans,
    Subtitle
} from 'autogen/translation-keys/trans-website-basket-samples-singlesample';
import { ButtonWithSpinner } from 'components/Button';
import Card from 'components/generic/card';
import { InlineButtonWithIcon } from 'components/generic/InlineButton';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import { QuickViewWithAbsoluteHeader, useQuickView } from 'components/quickView';
import ActionFooter from 'components/quickView/components/ActionFooter';
import format from 'helpers/translations';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import useSingleSampleSelect from './shared/hooks/useSingleSampleSelect';

/* #region styles */
const StyledCard = styled(Card)`
    background-color: ${ColorClubMatas5};
`;

const StyledCardContent = styled(Card.Content)`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing16};
    row-gap: ${Spacing8};
    align-items: center;
    padding-left: 128px;


    ${breakpoint.up(sizes.lg)} {
        padding-left: 104px;
        grid-template-columns:  1fr auto;
    }
`;

const Header = styled.div`
    background-color: ${ColorClubMatas5};
    width: 100%;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const TextContent = styled(Flex.Column)`
    padding: ${Spacing24};
`;

const ImageContainer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    max-width: 124px;
    height: 100%;
    width: 100%;

    & img {
        height: 100%
    }
`;

const StyledInlineButtonWithIcon = styled(InlineButtonWithIcon)`
    vertical-align: middle;
    margin-left: ${Spacing2};
    display: inline;
`;
/* #endregion */

export default function SingleSampleSelection({
    model: { popupImage, chooseImage }
}: { model: BasketDetailedSamplesViewModel }) {
    const { t } = useTranslation();
    const handleSingleSampleSelect = useSingleSampleSelect();
    const quickViewId = 'single-sample-disclaimer';
    const { toggle } = useQuickView(quickViewId);

    return (
        <>
            <StyledCard size={{ xs: 'md', lg: 'lg' }}>
                <StyledCardContent>
                    <ImageContainer>
                        <PictureDeprecated {...chooseImage} />
                    </ImageContainer>

                    <Flex.Column gap="0" alignItems="start">
                        <Typography.Headline size="M">{t(HeadlineTrans)}</Typography.Headline>

                        <Typography.Body as="span">
                            {t(ParagraphTrans)}{' '}
                            <StyledInlineButtonWithIcon
                                icon={<Icon type="Information" size={16} />}
                                size="sm"
                                onClick={() => toggle(true)}
                            />
                        </Typography.Body>
                    </Flex.Column>
                    <ButtonWithSpinner onClick={handleSingleSampleSelect}>{t(AddSample)}</ButtonWithSpinner>
                </StyledCardContent>
            </StyledCard>

            <QuickViewWithAbsoluteHeader
                quickViewId={quickViewId}
                position="right"
                footerRenderer={({ ...footerProps }) => (
                    <ActionFooter {...footerProps}>
                        <ButtonWithSpinner
                            size="lg"
                            onClick={async () => {
                                await handleSingleSampleSelect();
                                toggle(false);
                            }}
                        >
                            {t(AddSample)}
                        </ButtonWithSpinner>
                    </ActionFooter>
                )}
            >
                <Container>
                    <Header>
                        <PictureDeprecated {...popupImage} />
                    </Header>
                    <TextContent>
                        <Typography.Title size="S"> {t(HeadlineSingleTrans)}</Typography.Title>
                        <Typography.Body as="span" size="L">
                            {t(Subtitle)}
                        </Typography.Body>

                        <Typography.Body as="span">{format(t(Description))}</Typography.Body>
                    </TextContent>
                </Container>
            </QuickViewWithAbsoluteHeader>
        </>
    );
}
